/**
 * ChargeRecordPaymentDialog — collect the payment-capture metadata (mode,
 * date/time, proof-of-payment) for a single non-Plan member charge (F&B,
 * prints, stationery, booking, conference room) and mark it Paid.
 *
 * Extracted from account-detail.tsx (Phase 34 code — was a private,
 * non-exported function at lines 4233-4392) so a second surface — the
 * Member profile's charge-collection actions (Phase 39) — can reuse it
 * without duplicating the payment-capture form. account-detail.tsx's own 3
 * call sites are UNCHANGED behaviorally: they simply import this instead of
 * defining it locally, and never pass `additionalCharges`.
 *
 * Unlike RecordPaymentDialog (the Plan-schedule sibling extracted in Phase
 * 38-05), this dialog has NO partial-payment path — member_charges has no
 * amountPaid column, so every settle is `status: "Paid"` in full.
 *
 * Phase 39 addition: an optional `additionalCharges` prop. When the caller
 * (the member profile's grouped "Charges due" collect action) passes a
 * member's OTHER Pending charges alongside the primary one, this dialog
 * captures the payment metadata (mode/date/proof) ONCE via the existing
 * form and, right after the primary charge settles, marks every additional
 * charge Paid too (same metadata, all-or-report) — so ONE Record Payment
 * action clears the member's ENTIRE outstanding charges, not just the one
 * clicked. When `additionalCharges` is unset/empty, behavior is IDENTICAL
 * to the original single-charge dialog.
 *
 * Phase 39-08 addition (dual-rail settle): the member profile's "Charges
 * due" data spans TWO rails — member_charges (F&B/prints/stationery/etc,
 * settled via updateMemberCharge, above) AND charge-less Pay-Per-Use
 * stock_out rows (no member_charges row exists for them; settled by
 * PATCHing the stock_out itself — the SAME endpoint the shipped
 * MarkStockOutPaidDialog uses). An optional `stockOutTargets` prop carries
 * the latter. `charge` is now OPTIONAL (only the member-profile per-row
 * stock_out click omits it — account-detail.tsx's 3 call sites always pass
 * `charge` and never `stockOutTargets`, so they are byte-identically
 * unaffected). One captured payment mode/date/proof settles member_charges
 * first (charge + additionalCharges, unchanged) then every stock_out target
 * (all-or-report, mirrors the additionalCharges loop). There is no paidAt
 * field on UpdateStockOutBody — the captured date is display-only for the
 * stock_out rail (matches MarkStockOutPaidDialog).
 */
import { useState } from "react";
import {
  useUpdateMemberCharge,
  useUpdateStockOut,
  type MemberCharge,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { uploadPaymentProof } from "@/lib/upload";
import { Loader2, Upload } from "lucide-react";

// Charge payments accept only UPI or Cash (the Plan dialog keeps the full
// PAYMENT_MODES list). Proof is mandatory for UPI, not needed for Cash.
// Moved verbatim from account-detail.tsx (grep confirmed: used ONLY inside
// ChargeRecordPaymentDialog there).
const CHARGE_PAYMENT_MODES = ["UPI", "Cash"] as const;

// Re-declared locally — also defined (and used 20+ times) in
// account-detail.tsx. Tiny, dependency-free helper; duplicating avoids a
// cross-module import back into a page file (mirrors record-payment-dialog.tsx's
// own convention of re-declaring formatINR rather than importing it back).
function formatINR(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(amount)}`;
}

// Record / Edit Payment dialog for a member charge row. Mirrors
// RecordPaymentDialog (plan flow) but drops: partial toggle, amount input,
// nextDue input, markUnpaid, useMarkPlanPeriodPaid, useUpdatePlanPeriodReminder
// — member_charges has no amountPaid column, so every settle is full/status:"Paid".
export function ChargeRecordPaymentDialog({
  charge,
  additionalCharges = [],
  stockOutTargets = [],
  onClose,
}: {
  /** Now OPTIONAL — account-detail.tsx's 3 call sites always pass this;
   *  only the member-profile per-row stock_out click (39-09) omits it,
   *  relying on `stockOutTargets` instead. */
  charge?: MemberCharge;
  /** Phase 39 (Member profile grouped "Charges due" collect) — the member's
   *  OTHER Pending charges. When non-empty, each is marked Paid (same payment
   *  mode/date/proof captured for the primary charge) right after the
   *  primary charge settles, all-or-report. Unset/empty → behavior is
   *  IDENTICAL to the pre-Phase-39 single-charge dialog. */
  additionalCharges?: MemberCharge[];
  /** Phase 39-08 — charge-less Pay-Per-Use stock_out rows to settle by
   *  PATCHing paymentStatus:'Paid' on the stock_out itself (there is no
   *  member_charges row for these — updateMemberCharge cannot touch them).
   *  Settled AFTER charge + additionalCharges, same captured payment
   *  metadata, all-or-report. Unset/empty → behavior is IDENTICAL to the
   *  pre-39-08 dialog. */
  stockOutTargets?: { id: number; description: string; total: number }[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const updateCharge = useUpdateMemberCharge();
  const updateStockOut = useUpdateStockOut();
  // Map any legacy/other stored mode to UPI so the Select always has a match.
  // Guard `charge?.` — the stock_out-only path (39-09) has no MemberCharge.
  const [mode, setMode] = useState<string>(charge?.paymentMode === "Cash" ? "Cash" : "UPI");
  const [when, setWhen] = useState<string>(() =>
    charge?.paidAt
      ? new Date(charge.paidAt).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16)
  );
  const [proofUrl, setProofUrl] = useState<string | null>(charge?.paymentProofUrl ?? null);
  const [uploading, setUploading] = useState(false);

  // Phase 39 — aggregate outstanding across the primary charge + every
  // additional charge passed in (grouped collect = full clear of ALL of the
  // member's Pending non-Plan charges, not just the one clicked). Purely
  // additive/display — mirrors record-payment-dialog.tsx's additionalPeriods
  // banner (aggregateTotal). member_charges.total arrives as a Drizzle
  // numeric() string on the wire, so wrap with Number(...) before summing
  // (matches the existing Number(c.total) convention throughout this file).
  //
  // Phase 39-08 — `chargeTotal` is 0 (not synthesized) when `charge` is
  // absent so it never double-counts stockOutTargets[0] (see `primary`
  // below, used ONLY for header display, not this sum). `count`/
  // `aggregateTotal` span BOTH rails so the banner reflects the true total
  // across member_charges + stock_out targets.
  const chargeTotal = charge ? Number(charge.total) : 0;
  const additionalTotal = additionalCharges.reduce((s, c) => s + Number(c.total), 0);
  const stockOutTotal = stockOutTargets.reduce((s, t) => s + t.total, 0);
  const aggregateTotal = chargeTotal + additionalTotal + stockOutTotal;
  const count = (charge ? 1 : 0) + additionalCharges.length + stockOutTargets.length;

  // Header/title display only — the item the operator actually clicked.
  // Uses `charge` when present (all 3 account-detail call sites, plus any
  // member-profile grouped click that starts from a member_charge row);
  // else synthesizes from the first stock_out target (member-profile
  // per-row click on a charge-less Pay-Per-Use row, 39-09).
  const primary = charge
    ? { description: charge.description, total: chargeTotal }
    : { description: stockOutTargets[0]?.description ?? "Charges", total: stockOutTargets[0]?.total ?? 0 };

  async function onProof(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadPaymentProof(file);
      if (r.ok) {
        setProofUrl(r.url);
        toast({ title: "Proof of payment uploaded." });
      } else {
        toast({
          title: "Couldn't upload proof",
          description: r.message || `Upload failed (HTTP ${r.status}).`,
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Couldn't upload proof",
        description: (err as Error)?.message ?? "Network error — check your connection.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }

  // Proof of payment is mandatory for UPI; not needed for Cash.
  const proofRequired = mode === "UPI";

  async function save() {
    // P4 guard — mode + date are required.
    if (!mode) {
      toast({ title: "Select a payment mode.", variant: "destructive" });
      return;
    }
    if (!when) {
      toast({ title: "Enter the payment date.", variant: "destructive" });
      return;
    }
    if (proofRequired && !proofUrl) {
      toast({ title: "Proof of payment is required for UPI.", variant: "destructive" });
      return;
    }
    try {
      // Running settled counter spans BOTH rails (member_charges +
      // stock_out) so a failure at any point can report an accurate
      // "Settled N of count" progress message.
      let settled = 0;

      if (charge) {
        await updateCharge.mutateAsync({
          id: charge.id,
          data: {
            status: "Paid",
            paymentMode: mode,
            paidAt: new Date(when).toISOString(),
            paymentProofUrl: proofUrl,
          },
        });
        settled++;
      }

      // Phase 39 — settle every additional Pending charge (grouped collect =
      // clear the WHOLE outstanding, not just the one clicked). Same payment
      // metadata captured above, full each (member_charges has no
      // amountPaid/partial column — every settle is status:"Paid"),
      // all-or-report: stop and report progress on the first failure rather
      // than silently skipping the rest (mirrors record-payment-dialog.tsx's
      // additionalPeriods loop).
      for (const extra of additionalCharges) {
        try {
          await updateCharge.mutateAsync({
            id: extra.id,
            data: {
              status: "Paid",
              paymentMode: mode,
              paidAt: new Date(when).toISOString(),
              paymentProofUrl: proofUrl,
            },
          });
          settled++;
        } catch {
          toast({
            title: "Cleared some, not all.",
            description: `Settled ${settled} of ${count} charges — the rest still need collecting.`,
            variant: "destructive",
          });
          onClose();
          return;
        }
      }

      // Phase 39-08 — settle every stock_out-rail target (charge-less
      // Pay-Per-Use rows with no member_charges row to update) by PATCHing
      // the stock_out itself — the SAME endpoint the shipped
      // MarkStockOutPaidDialog uses. Same captured payment metadata,
      // all-or-report (mirrors the additionalCharges loop above). No
      // paidAt/date field exists on UpdateStockOutBody — the captured
      // `when` is display-only for this rail.
      for (const t of stockOutTargets) {
        try {
          await updateStockOut.mutateAsync({
            id: t.id,
            data: {
              paymentStatus: "Paid",
              paymentMethod: mode,
              paymentProofUrl: proofUrl,
            },
          });
          settled++;
        } catch {
          toast({
            title: "Cleared some, not all.",
            description: `Settled ${settled} of ${count} charges — the rest still need collecting.`,
            variant: "destructive",
          });
          onClose();
          return;
        }
      }

      if (count > 1) {
        toast({ title: `Cleared ${count} charges.` });
      } else {
        toast({
          title: charge?.status === "Paid" ? "Payment updated." : "Payment recorded.",
        });
      }
      onClose();
    } catch {
      toast({ title: "Couldn't record the payment.", variant: "destructive" });
    }
  }

  const isEdit = charge?.status === "Paid";

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Payment" : "Record Payment"}</DialogTitle>
          <DialogDescription>
            {primary.description.replace(/_/g, " ")} · {formatINR(primary.total)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Phase 39 — Member profile grouped "Charges due" collect:
              aggregate banner across ALL of this member's Pending charges
              (this one + additionalCharges) AND (Phase 39-08) any
              charge-less stock_out targets. Purely additive — only renders
              when the caller passed extras; the single-charge
              account-detail.tsx flow never sees this block. */}
          {count > 1 && (
            <div className="space-y-1 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm">
              <div className="flex justify-between font-semibold">
                <span>Outstanding · {count} charges</span>
                <span className="tabular-nums">{formatINR(aggregateTotal)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                This charge {formatINR(primary.total)} + {count - 1} more charge(s) {formatINR(aggregateTotal - primary.total)} — the payment mode/date/proof below settles ALL of them in full.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Payment mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHARGE_PAYMENT_MODES.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date &amp; time</Label>
              <Input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">
              Proof of payment {proofRequired ? "(required)" : "(optional)"}
            </Label>
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm" disabled={uploading}>
                <label className="cursor-pointer">
                  {uploading
                    ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    : <Upload className="mr-1.5 h-4 w-4" />}
                  {proofUrl ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => onProof(e.target.files?.[0])}
                  />
                </label>
              </Button>
              {proofUrl && (
                <a href={proofUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">
                  View uploaded proof
                </a>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={save}
            disabled={updateCharge.isPending || updateStockOut.isPending || uploading || (proofRequired && !proofUrl)}
            title={proofRequired && !proofUrl ? "Upload proof of payment for UPI" : undefined}
          >
            {(updateCharge.isPending || updateStockOut.isPending) ? "Saving…" : isEdit ? "Update Payment" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
